import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import quantamCross from "../../../assets/images/quantamCross.jpeg";
import icon4 from "../../../assets/images/Asset4.png";
import { fetchGlobalSearch } from "../../../api";

const emptyResults = {
  jobs: [],
  projects: [],
  people: [],
  locations: [],
};

function SearchContainer({ isMenuSelected }) {
  const navigate = useNavigate();
  const [searchInput, setSearchInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState(null);
  const [results, setResults] = useState(emptyResults);

  const runSearch = useCallback(async (raw) => {
    const q = String(raw ?? "").trim();
    setErrorMsg(null);
    if (!q) {
      setResults(emptyResults);
      return;
    }
    setLoading(true);
    try {
      const data = await fetchGlobalSearch(q, 3);
      const arr = (d, a, b) =>
        Array.isArray(d?.[a]) ? d[a] : Array.isArray(d?.[b]) ? d[b] : [];
      setResults({
        jobs: arr(data, "jobs", "Jobs"),
        projects: arr(data, "projects", "Projects"),
        people: arr(data, "people", "People"),
        locations: arr(data, "locations", "Locations"),
      });
    } catch (e) {
      setErrorMsg(e.message || "Search failed.");
      setResults(emptyResults);
    } finally {
      setLoading(false);
    }
  }, []);

  /** Debounced search while typing — case folding is server-side */
  useEffect(() => {
    const q = searchInput.trim();
    if (!q) {
      setResults(emptyResults);
      setErrorMsg(null);
      return undefined;
    }
    const t = setTimeout(() => runSearch(q), 400);
    return () => clearTimeout(t);
  }, [searchInput, runSearch]);

  const handleSearch = () => {
    const q = searchInput.trim();
    if (!q) return;
    runSearch(q);
  };

  const handleResultNavigate = (item) => {
    if (!item) return;
    switch (item.type) {
      case "job":
        navigate(`/job-details/${item.id}`);
        break;
      case "project":
        navigate(`/project-listing?projectId=${encodeURIComponent(item.id)}`);
        break;
      case "person":
        navigate(`/our-people?personId=${encodeURIComponent(item.id)}`);
        break;
      default:
        break;
    }
    if (item.kind === "presence") {
      navigate(`/about-us?presenceId=${encodeURIComponent(item.id)}`);
      isMenuSelected?.();
      return;
    }
    if (item.kind === "catalog") {
      navigate(
        `/project-listing?location=${encodeURIComponent(item.title || "")}`
      );
      isMenuSelected?.();
      return;
    }
    isMenuSelected?.();
  };

  const clearSearch = () => {
    setSearchInput("");
    setResults(emptyResults);
    setErrorMsg(null);
  };

  const sectionLabel = {
    jobs: "Jobs",
    projects: "Projects",
    people: "People",
    locations: "Locations",
  };

  const CATEGORY_ORDER = ["jobs", "projects", "people", "locations"];

  const renderList = (key) => {
    const items = results[key];
    if (!items || items.length === 0) return null;
    const isLocationBucket = key === "locations";

    return (
      <div className="search-category-column min-w-0">
        <p
          className="text-uppercase mb-2"
          style={{
            fontSize: "17px",
            fontWeight: "600",
            color: "#C82027",
            fontFamily: "Avenir",
          }}
        >
          {sectionLabel[key]}
        </p>
        <ul className="list-unstyled mb-0">
          {items.map((item, idx) => {
            const title =
              item.title ||
              (isLocationBucket ? "" : `[${key.slice(0, -1)} #${item.id}]`);
            const sub = item.subtitle;
            const onClickRow = () => {
              if (isLocationBucket && item.kind) {
                handleResultNavigate(item);
              } else {
                handleResultNavigate(item);
              }
            };
            return (
              <li
                key={`${key}-${item.id ?? idx}-${title}`}
                className="searchSuggestion py-2 border-bottom"
                style={{
                  cursor: "pointer",
                  borderColor: "#e0e0e0",
                }}
                onClick={onClickRow}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") onClickRow();
                }}
                role="button"
                tabIndex={0}
              >
                <span style={{ fontFamily: '"EB Garamond"', fontSize: "22px" }}>
                  {title}
                </span>
                {sub ? (
                  <div
                    style={{
                      fontSize: "15px",
                      color: "#6b6b6b",
                      fontFamily: "Avenir",
                      marginTop: "4px",
                    }}
                  >
                    {sub}
                  </div>
                ) : null}
              </li>
            );
          })}
        </ul>
      </div>
    );
  };

  const hasAny =
    (results.jobs && results.jobs.length > 0) ||
    (results.projects && results.projects.length > 0) ||
    (results.people && results.people.length > 0) ||
    (results.locations && results.locations.length > 0);

  return (
    <>
      <div
        className="d-flex justify-content-between align-items-center mb-4  border-top-2 border-bottom-2 "
        style={{ borderColor: "#ccc" }}
      >
        <div className="container d-flex align-items-center py-4">
          <div className="input-group  " style={{ width: "100%" }}>
            <span className="input-group-text bg-transparent border-0 pl-0">
              <img className="ml-3 " src={icon4} alt="" />
            </span>
            <input
              type="search"
              autoComplete="off"
              className="form-control border-0"
              placeholder="Search jobs, projects, people…"
              style={{
                backgroundColor: "#F5F4F1",
                fontSize: "36px",
                outline: "none",
                boxShadow: "none",
                fontFamily: '"EB Garamond"',
              }}
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") handleSearch();
              }}
            />
            <div
              className=" p-2"
              style={{ display: "flex", alignItems: "center" }}
            >
              <img
                onClick={clearSearch}
                src={quantamCross}
                className="pr-3 "
                alt="Clear search"
                style={{ width: "34px", height: "18px", cursor: "pointer" }}
              />
            </div>
          </div>
          <button
            className="btn btn-dark px-4 mr-5 font17"
            style={{ borderRadius: "20px" }}
            type="button"
            onClick={handleSearch}
            disabled={loading}
          >
            {loading ? "…" : "Search"}
          </button>
        </div>
      </div>

      <div className="" style={{ backgroundColor: "#F5F4F1", padding: "20px" }}>
        <div className="container ">
          <p
            className="text-uppercase"
            style={{
              fontSize: "20px",
              fontWeight: "500",
              color: "#7C7C7C",
              fontFamily: "Avenir",
            }}
          >
            {searchInput.trim() ? "Results" : "Suggested"}
          </p>
          {errorMsg ? (
            <p style={{ color: "#b00020" }}>{errorMsg}</p>
          ) : !searchInput.trim() ? (
            <p style={{ color: "#7C7C7C", fontFamily: "Avenir" }}>
              Type to find jobs, projects, people, and locations (not
              case-sensitive).
            </p>
          ) : loading && !hasAny ? (
            <p style={{ color: "#7C7C7C" }}>Searching…</p>
          ) : !hasAny ? (
            <p style={{ color: "#7C7C7C" }}>No matches yet. Try other words.</p>
          ) : (
            <div
              className="search-results-categories-grid"
              style={{
                display: "grid",
                gap: "1.25rem 1.75rem",
                gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
                alignItems: "start",
              }}
            >
              {CATEGORY_ORDER.map((key) => {
                const block = renderList(key);
                if (!block) return null;
                return (
                  <div key={`col-${key}`} className="min-w-0">
                    {block}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </>
  );
}

export default SearchContainer;
